import { generateCanonicalUrl } from 'utils/urls/canonicalUrls';
import { getTenantById } from '@tenants/manager';
import { getTenantSeoConfig } from '@tenants/utils/configs';
import type { TenantId } from '@tenants/types';
import type { HrefLangLink } from 'components/pages/area-listings/util/head-tags-builder';
import type { CountryCode } from 'types/countries';
import { SiteLanguage } from 'types/locales';
import { removeLocalePrefix } from 'utils/locale/get-localized-url';

export interface GenerateHreflangFromCanonicalUrlParams {
  canonicalUrl: string;
  locale: string;
  tenantId: TenantId;
}

/**
 * Generates hreflang tags from canonical URL. Supports multi-locale (en-CA/fr-CA) and multi-country (en-CA/en-US).
 * Always includes x-default pointing to default version.
 * Uses new locale-aware logic if useLocaleAwareHrefLang is enabled, otherwise uses legacy behavior.
 */
export function generateHreflangFromCanonicalUrl(params: GenerateHreflangFromCanonicalUrlParams): HrefLangLink[] {
  const { canonicalUrl, locale, tenantId } = params;
  
  // Check if tenant has locale-aware hreflang enabled
  const tenant = getTenantById(tenantId);
  const seoConfig = getTenantSeoConfig(tenant);
  
  if (!seoConfig?.useLocaleAwareHrefLang) {
    return generateLegacyHreflangs({ canonicalUrl, locale });
  }
  
  const supportedLanguages = tenant.localization.languages as SiteLanguage[];
  const supportedCountries = tenant.localization.countries as CountryCode[];
  const defaultLanguage = tenant.localization.defaultLanguage;
  const defaultCountry = tenant.localization.defaultCountry;
  
  // Determine if this is multi-country or multi-locale
  const isMultiCountry = supportedCountries.length > 1 && supportedLanguages.length === 1;
  const isMultiLocale = supportedLanguages.length > 1;
  
  let hrefLangs: HrefLangLink[] = [];
  
  if (isMultiCountry) {
    // Multi-country case (e.g., Zoocasa: same language, different countries)
    hrefLangs = generateMultiCountryHreflangs({
      canonicalUrl,
      locale,
      supportedCountries,
      defaultCountry,
      tenantId,
    });
  } else if (isMultiLocale) {
    // Multi-locale case (e.g., eXp Realty CA: same country, different languages)
    hrefLangs = generateMultiLocaleHreflangs({
      canonicalUrl,
      locale,
      supportedLanguages,
      defaultLanguage,
      tenantId,
    });
  }
  
  return hrefLangs;
}

/** Legacy hreflang generation: simple CA/US country suffixes pointing to same canonical URL */
function generateLegacyHreflangs(params: {
  canonicalUrl: string;
  locale: string;
}): HrefLangLink[] {
  const { canonicalUrl, locale } = params;
  
  return [
    { href: canonicalUrl, hrefLang: `${locale}-ca`.toLowerCase() },
    { href: canonicalUrl, hrefLang: `${locale}-us`.toLowerCase() },
  ];
}

/** Generates hreflang for multi-country (same language, different countries). All variants point to same canonical URL. */
function generateMultiCountryHreflangs(params: {
  canonicalUrl: string;
  locale: string;
  supportedCountries: CountryCode[];
  defaultCountry: CountryCode;
  tenantId: TenantId;
}): HrefLangLink[] {
  const { canonicalUrl, locale, supportedCountries } = params;
  
  // Extract language from locale (e.g., 'en-CA' -> 'en')
  const language = locale.split('-')[0].toLowerCase();
  
  const hrefLangs: HrefLangLink[] = supportedCountries.map(country => {
    // For multi-country, all variants point to the same canonical URL
    // (The canonical URL already represents the correct country context)
    const hrefLang = `${language}-${country.toLowerCase()}`;
    
    return { hrefLang, href: canonicalUrl };
  });
  
  // Add x-default pointing to default country
  hrefLangs.push({
    hrefLang: 'x-default',
    href: canonicalUrl,
  });
  
  return hrefLangs;
}

/**
 * Generates hreflang for multi-locale (same country, different languages).
 * Derives alternate language URLs from canonical by stripping locale prefix and regenerating for each language.
 * 
 * @example
 * // Input: canonicalUrl='https://example.com/fr-CA/toronto-homes', supportedLanguages=['en-CA', 'fr-CA']
 * // Output: [
 * //   { hrefLang: 'en-ca', href: 'https://example.com/en-ca/toronto-homes' },
 * //   { hrefLang: 'fr-ca', href: 'https://example.com/fr-ca/toronto-homes' },
 * //   { hrefLang: 'x-default', href: 'https://example.com/en-ca/toronto-homes' }
 * // ]
 */
function generateMultiLocaleHreflangs(params: {
  canonicalUrl: string;
  locale: string;
  supportedLanguages: SiteLanguage[];
  defaultLanguage: SiteLanguage;
  tenantId: TenantId;
}): HrefLangLink[] {
  const { canonicalUrl, supportedLanguages, defaultLanguage, tenantId } = params;
  
  const url = new URL(canonicalUrl);
  const baseUrl = `${url.protocol}//${url.host}`;
  const cleanPath = removeLocalePrefix(url.pathname, supportedLanguages);
  
  const hrefLangs = supportedLanguages.map(lang => ({
    hrefLang: lang.toLowerCase(),
    href: generateCanonicalUrl({
      asPath: cleanPath,
      baseHost: baseUrl,
      locale: lang,
      tenantId,
    }),
  }));
  
  hrefLangs.push({
    hrefLang: 'x-default',
    href: generateCanonicalUrl({
      asPath: cleanPath,
      baseHost: baseUrl,
      locale: defaultLanguage,
      tenantId,
    }),
  });
  
  return hrefLangs;
}

